import React, { CSSProperties, useEffect, useState } from 'react';
import {
  Table,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  TableHead,
  TableFooter,
} from '@material-ui/core';
import LinkIcon from '@mui/icons-material/Link';
import { Link } from 'react-router-dom';
import { Map } from 'immutable';
import moment from 'moment';
import { CircularProgress, Chip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDispatch, useSelector } from 'react-redux';

import { selectUser } from 'entities/user/selectors';
import { PureRender } from 'utils/component-helpers';
import Campaign from 'entities/campaign/model';
import { TableBox } from 'retired/Box';
import Img from 'retired/Img';
import ExternalLinkIcon from 'assets/icons/external-link.svg';
import { getAppDomain } from 'utils/getAppDomain';
import DialogButton from 'retired/DialogButton';

import { setOptInStatusAction } from './types';
import { selectOptInStatusLoading } from './selectors';

const styleForDate: CSSProperties = {
  whiteSpace: 'nowrap',
};

interface HeaderColumnInterface {
  id: string;
  label: string;
  sorted?: boolean;
  campaigns: Campaign[];
}

interface Props {
  campaigns: Map<string, Campaign>;
  tab: 'active' | 'inactive' | 'shared';
}

const CampaignsTable = (props: Props): JSX.Element => {
  const { campaigns, tab } = props;

  const dispatch = useDispatch();
  const optInStatusLoading = useSelector(selectOptInStatusLoading);
  const user = useSelector(selectUser);

  const getSorted = (
    sortedBy: string,
    order: string,
    unSorted: Campaign[] = campaigns ? campaigns.toArray() : []
  ): Campaign[] => {
    if (sortedBy === 'title') {
      return unSorted.sort((a: Campaign, b: Campaign) =>
        order === 'desc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title)
      );
    } else {
      return unSorted.sort((a: Campaign, b: Campaign) => {
        const first = a[sortedBy] ? new Date(a[sortedBy]).getTime() : 0;
        const second = b[sortedBy] ? new Date(b[sortedBy]).getTime() : 0;
        return order === 'desc' ? first - second : second - first;
      });
    }
  };

  const columns: any[] = [
    { id: 'title', label: 'Title', sorted: true },
    { id: 'createdAt', label: 'Created Date', sorted: true },
    { id: 'startingAt', label: 'Start Date', sorted: true },
    { id: 'endingAt', label: 'End date', sorted: true },
    user?.organization?.parent && { id: 'source', label: 'Source' },
    tab !== 'shared' && { id: 'duplicate', label: 'Duplicate' },
    tab === 'active' && { id: 'view', label: 'View in App' },
    user?.organization?.parent && { id: 'actions', label: tab === 'shared' ? 'Add' : 'Remove' },
  ].filter(Boolean);

  const [values, setValues] = useState({
    sortedBy: 'startingAt',
    order: 'asc',
    sortedCampaigns: getSorted('startingAt', 'asc'),
  });

  const onSortedLabelClick = (item: HeaderColumnInterface): void => {
    if (item.id === values.sortedBy) {
      const order = values.order === 'desc' ? 'asc' : 'desc';
      setValues({
        sortedBy: item.id,
        order,
        sortedCampaigns: getSorted(item.id, order),
      });
    } else {
      setValues({
        sortedBy: item.id,
        order: 'desc',
        sortedCampaigns: getSorted(item.id, 'desc'),
      });
    }
  };

  useEffect(() => {
    setValues({
      sortedBy: 'startingAt',
      order: 'asc',
      sortedCampaigns: getSorted('startingAt', 'asc'),
    });
  }, [campaigns]);

  const displayDate = (date?: Date): string => (date ? moment(date).format('MMM DD, YYYY') : '-');
  return (
    <TableBox>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.filter(Boolean).map((item: HeaderColumnInterface) => (
              <TableCell key={item.id} size="medium" style={styleForDate}>
                {item.sorted ? (
                  <TableSortLabel
                    direction={values.order}
                    active={values.sortedBy === item.id}
                    onClick={(): void => {
                      onSortedLabelClick(item);
                    }}
                  >
                    {item.label}
                  </TableSortLabel>
                ) : (
                  item.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {campaigns && campaigns.size ? (
          <TableBody>
            {values.sortedCampaigns.map((campaign: Campaign) => (
              <TableRow key={campaign.id}>
                <TableCell size="medium">
                  {campaign.isSharedWithSubsidiary ? (
                    campaign.title
                  ) : (
                    <Link to={`/campaigns/edit/${campaign.id}`}>{campaign.title}</Link>
                  )}
                  {campaign.linkOnly && (
                    <span
                      style={{
                        marginLeft: 10,
                        position: 'relative',
                      }}
                    >
                      <LinkIcon
                        style={{
                          fontSize: 20,
                          position: 'absolute',
                          left: 0,
                          top: -4,
                        }}
                      />
                    </span>
                  )}

                  {campaign.autoTagDonations && (
                    <Chip style={{ marginLeft: 12 }} label="Universal Campaign" />
                  )}
                </TableCell>
                <TableCell size="medium" style={styleForDate}>
                  {displayDate(campaign.createdAt)}
                </TableCell>
                <TableCell size="medium" style={styleForDate}>
                  {displayDate(campaign.startingAt)}
                </TableCell>
                <TableCell size="medium" style={styleForDate}>
                  {displayDate(campaign.endingAt)}
                </TableCell>
                {user?.organization?.parent && (
                  <TableCell size="medium">
                    {campaign.isSharedWithSubsidiary && <div>{campaign.company?.name}</div>}
                  </TableCell>
                )}
                {tab !== 'shared' && (
                  <TableCell size="medium">
                    {campaign.company?.id === user?.organization.id && (
                      <Link
                        to={`/campaigns/edit/${campaign.id}?duplicate=1`}
                        style={{
                          padding: 20,
                        }}
                      >
                        <ContentCopyIcon />
                      </Link>
                    )}
                  </TableCell>
                )}
                {tab === 'active' && (
                  <TableCell size="medium">
                    <a
                      href={`${getAppDomain([campaign.company])}/campaign/${campaign.id}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        padding: 20,
                      }}
                    >
                      <Img
                        src={ExternalLinkIcon}
                        alt="External Link Icon"
                        style={{
                          width: 16,
                          height: 16,
                          marginLeft: 5,
                        }}
                      />
                    </a>
                  </TableCell>
                )}
                {user?.organization?.parent && (
                  <TableCell size="medium">
                    {campaign.isSharedWithSubsidiary && (
                      <div>
                        {optInStatusLoading ? (
                          <CircularProgress size={24} style={{ margin: '9px 12px 8px' }} />
                        ) : campaign.optedIn ? (
                          <div style={{ marginLeft: -7 }}>
                            <DialogButton
                              title="Remove this campaign from your organization"
                              text="Are you sure you want to remove this campaign? It will no longer be visible to your employees. Note, all tagged deeds will remain on your environment, and must be removed from the deed details page."
                              confirmLabel="Remove"
                              cancelLabel="Go Back"
                              buttonProps={{
                                variant: 'text',
                              }}
                              handleConfirm={() => {
                                dispatch(setOptInStatusAction(campaign, user, false));
                              }}
                            >
                              <BlockIcon />
                            </DialogButton>
                          </div>
                        ) : (
                          <div style={{ marginLeft: -17 }}>
                            <DialogButton
                              title="Add this campaign to your organization"
                              text="Are you sure you want to add this campaign and all tagged opportunities to your organization? This campaign, along with all tagged opportunities will become visible to all your employees."
                              confirmLabel="Add"
                              cancelLabel="Go Back"
                              buttonProps={{
                                variant: 'text',
                              }}
                              handleConfirm={() => {
                                dispatch(setOptInStatusAction(campaign, user, true));
                              }}
                            >
                              <CheckIcon />
                            </DialogButton>
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        ) : (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} style={{ textAlign: 'center', padding: '20px' }}>
                You have no {tab} campaigns
              </TableCell>
            </TableRow>
          </TableBody>
        )}
        <TableFooter>
          <TableRow>
            <TableCell
              size="medium"
              colSpan={columns.length}
              style={{
                color: '#0000008a',
                fontSize: '0.875rem',
              }}
            >
              Total: {values.sortedCampaigns.length}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableBox>
  );
};

export default PureRender(CampaignsTable);
